/**
 * Environment detection and extension communication utilities
 */

// Chrome extension type declaration
declare global {
  interface Window {
    chrome?: {
      runtime?: {
        id?: string;
        lastError?: {
          message: string;
        };
        sendMessage: (
          message: any,
          callback?: (response: any) => void
        ) => void;
        onMessage: {
          addListener: (
            callback: (
              message: any,
              sender: any,
              sendResponse: (response?: any) => void
            ) => void
          ) => void;
          removeListener: (
            callback: (
              message: any,
              sender: any,
              sendResponse: (response?: any) => void
            ) => void
          ) => void;
        };
      };
    };
  }
}

// Check if the app is running inside a webview/iframe
export const isEmbedded = (): boolean => {
  return window !== window.top || 
         window.parent !== window || 
         !!document.querySelector('meta[name="embedded-view"]');
};

// Check if running inside a Chrome extension
export const isInExtension = (): boolean => {
  return typeof window.chrome !== 'undefined' && 
         !!window.chrome.runtime && 
         !!window.chrome.runtime.id;
};

// Detect if we can use the extension messaging API
export const canUseExtensionMessaging = (): boolean => {
  return isEmbedded() && isInExtension();
};

// Send a message to the parent extension and get a response
export const sendExtensionMessage = async <T>(
  message: any
): Promise<T> => {
  return new Promise((resolve, reject) => {
    try {
      // Make sure we're in an extension context
      if (!canUseExtensionMessaging() || !window.chrome?.runtime) {
        throw new Error('Not in an extension context');
      }
      
      // Send message to extension background script
      window.chrome.runtime.sendMessage(message, (response: any) => {
        if (window.chrome?.runtime?.lastError) {
          reject(new Error(window.chrome.runtime.lastError.message));
        } else if (response && response.error) {
          reject(new Error(response.error));
        } else {
          resolve(response as T);
        }
      });
    } catch (error) {
      reject(error);
    }
  });
};

// Listen for messages from the extension
export const addExtensionMessageListener = (
  handler: (message: any, sender: any) => void | Promise<void>
): (() => void) => {
  const listener = async (
    message: any,
    sender: any,
    sendResponse: (response?: any) => void
  ) => {
    try {
      // Process message
      const result = await handler(message, sender);
      sendResponse({ success: true, data: result });
    } catch (error) {
      sendResponse({ 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
    }
  };
  
  // Add the listener if Chrome is available
  if (window.chrome?.runtime) {
    window.chrome.runtime.onMessage.addListener(listener);
    
    // Return a function to remove the listener
    return () => {
      if (window.chrome?.runtime) {
        window.chrome.runtime.onMessage.removeListener(listener);
      }
    };
  }
  
  // Return a no-op if Chrome is not available
  return () => {};
};

// Alternative message passing for non-extension embedded contexts
export const sendPostMessage = <T>(
  message: any, 
  targetOrigin = '*'
): Promise<T> => {
  return new Promise((resolve, reject) => {
    // Generate a unique ID for this message
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Add the ID to the message
    const messageWithId = {
      ...message,
      messageId
    };
    
    // Function to handle the response
    const handleResponse = (event: MessageEvent) => {
      // Check that the message is a response to our request
      if (
        event.data && 
        event.data.type === 'response' && 
        event.data.messageId === messageId
      ) {
        // Remove the listener
        window.removeEventListener('message', handleResponse);
        
        // Resolve or reject based on the response
        if (event.data.error) {
          reject(new Error(event.data.error));
        } else {
          resolve(event.data.data as T);
        }
      }
    };
    
    // Add a listener for the response
    window.addEventListener('message', handleResponse);
    
    // Send the message
    window.parent.postMessage(messageWithId, targetOrigin);
    
    // Set a timeout to reject the promise if no response is received
    setTimeout(() => {
      window.removeEventListener('message', handleResponse);
      reject(new Error('No response received within timeout'));
    }, 30000); // 30 second timeout
  });
}; 